import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonService } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '@shared';
import { getColorFromEmail } from '@shared/helpers/user-avatar-color-generator';

export interface ShareDialogData {
  name: string;
  owner: string;
  editors: string[];
  viewers: string[];
  saveFn: (updates: ShareDialogResult) => Promise<void>;
}

export interface ShareDialogResult {
  owner: string;
  editors: string[];
  viewers: string[];
}

@Component({
  selector: 'app-share-resource',
  templateUrl: './share-resource.component.html',
  styleUrl: './share-resource.component.scss'
})
export class ShareResourceComponent implements OnInit {

  sharedWith: SharedWith[] = [];
  userAccessEntries: UserAccessEntry[] = [];
  entriesAccessLevel: AccessLevel = AccessLevel.Editor;

  users: User[] = [];
  notSelectedUsers: User[] = [];
  accessLevelEnum = AccessLevel;
  usersLoading: boolean = false;
  hasChanges: boolean = false;
  saveLoading: boolean = false;

  private initialOwner: string = '';
  private initialEditors: string[] = [];
  private initialViewers: string[] = [];

  constructor(
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<ShareResourceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareDialogData
  ) { }

  ngOnInit(): void {
    // Store initial state
    this.initialOwner = this.data.owner;
    this.initialEditors = [...(this.data.editors || [])];
    this.initialViewers = [...(this.data.viewers || [])];
    this.loadAllUsers();
  }

  onEntriesUsersChanged(users: User[]) {
    this.userAccessEntries = users.map(user => {
      return {
        email: user.email,
        accessLevel: this.entriesAccessLevel
      };
    });
  }

  onEntriesAccessLevelChanged() {
    this.userAccessEntries = this.userAccessEntries.map(entry => {
      return {
        ...entry,
        accessLevel: this.entriesAccessLevel
      };
    });
  }

  transferOwnership(email: string) {
    const previousOwner = this.sharedWith.find(user => user.accessLevel === AccessLevel.Owner)!.email;
    this.sharedWith = [
      this.mapUserToSharedWith(email, AccessLevel.Owner),
      ...this.sharedWith
        .filter(user => user.email !== email && user.email !== previousOwner)
        .map(user => this.mapUserToSharedWith(user.email, user.accessLevel)),
      this.mapUserToSharedWith(previousOwner, AccessLevel.Editor)
    ];
    this.detectChanges();
  }

  removeUser(email: string) {
    this.sharedWith = this.sharedWith.filter(user => user.email !== email);
    this.detectChanges();
  }

  onSave() {
    if (!this.hasChanges) {
      this.dialogRef.close();
      return;
    }

    this.saveLoading = true;
    const result: ShareDialogResult = {
      owner: this.sharedWith.find(user => user.accessLevel === AccessLevel.Owner)!.email,
      editors: this.sharedWith.filter(user => user.accessLevel === AccessLevel.Editor).map(user => user.email),
      viewers: this.sharedWith.filter(user => user.accessLevel === AccessLevel.Viewer).map(user => user.email)
    };

    this.data.saveFn(result).then(() => {
      this.saveLoading = false;
      this.snackBar.open(this.translateService.instant('alerts.savedSuccessfully'), '✖', { direction: this.translateService.instant('uiDirection') });
      this.dialogRef.close(result);
    }).catch(() => {
      this.saveLoading = false;
      this.snackBar.open(this.translateService.instant('alerts.saveFailed'), '✖', { direction: this.translateService.instant('uiDirection') });
    });
  }

  onShare() {
    this.saveLoading = true;
    const result: ShareDialogResult = {
      owner: this.sharedWith.find(user => user.accessLevel === AccessLevel.Owner)!.email,
      editors: [
        ...this.sharedWith.filter(user => user.accessLevel === AccessLevel.Editor).map(user => user.email),
        ...this.userAccessEntries.filter(entry => entry.accessLevel === AccessLevel.Editor).map(entry => entry.email)
      ],
      viewers: [
        ...this.sharedWith.filter(user => user.accessLevel === AccessLevel.Viewer).map(user => user.email),
        ...this.userAccessEntries.filter(entry => entry.accessLevel === AccessLevel.Viewer).map(entry => entry.email)
      ]
    };

    this.data.saveFn(result).then(() => {
      this.saveLoading = false;
      this.snackBar.open(this.translateService.instant('alerts.sharedSuccessfully'), '✖', { direction: this.translateService.instant('uiDirection') });
      this.dialogRef.close(result);
    }).catch(() => {
      this.saveLoading = false;
      this.snackBar.open(this.translateService.instant('alerts.saveFailed'), '✖', { direction: this.translateService.instant('uiDirection') });
    });
  }

  detectChanges() {
    const currentOwner = this.sharedWith.find(user => user.accessLevel === AccessLevel.Owner)?.email || '';
    const currentEditors = this.sharedWith
      .filter(user => user.accessLevel === AccessLevel.Editor)
      .map(user => user.email)
      .sort();
    const currentViewers = this.sharedWith
      .filter(user => user.accessLevel === AccessLevel.Viewer)
      .map(user => user.email)
      .sort();

    this.hasChanges =
      currentOwner !== this.initialOwner ||
      !this.areArraysEqual(currentEditors, [...this.initialEditors].sort()) ||
      !this.areArraysEqual(currentViewers, [...this.initialViewers].sort());
  }

  private async loadAllUsers() {
    this.usersLoading = true;
    const res = await this.commonService.getAllUsersAsync();
    this.users = res ? res : [];
    this.notSelectedUsers = this.users.filter(user => !this.data.editors?.includes(user.email) && !this.data.viewers?.includes(user.email) && user.email !== this.data.owner);
    this.sharedWith = [
      this.mapUserToSharedWith(this.data.owner, AccessLevel.Owner),
      ...(this.data.editors?.map(editor => this.mapUserToSharedWith(editor, AccessLevel.Editor)) || []),
      ...(this.data.viewers?.map(viewer => this.mapUserToSharedWith(viewer, AccessLevel.Viewer)) || [])
    ];
    this.usersLoading = false;
  }

  private mapUserToSharedWith(email: string, accessLevel: AccessLevel): SharedWith {
    const user = this.users.find(user => user.email === email);
    return {
      name: user?.username || email.split('@')[0],
      email: email,
      userInitals: email.charAt(0).toLocaleUpperCase(),
      avatarBackgroundColor: getColorFromEmail(email),
      accessLevel: accessLevel
    };
  }

  private areArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  }

}

enum AccessLevel {
  Owner = 1,
  Editor = 2,
  Viewer = 3
}

interface SharedWith {
  name: string;
  email: string;
  userInitals: string;
  avatarBackgroundColor: string;
  accessLevel: AccessLevel;
}

interface UserAccessEntry {
  email: string;
  accessLevel: AccessLevel;
}
